import {
	createPresignedDownloadUrl,
	type AccessibleObject,
} from "@ptx-autonomous/libs/s3";
import { S3Client } from "@aws-sdk/client-s3";
import type { PlaylistItem } from "@shared/components/Files/VideoPlayer.vue";
import type { VideoUrl } from "@shared/composables/help-files";
import { getQualityVariantsFromList } from "@shared/utils/video-quality-keys";

/**
 * Builds the playable URL for a video key: a single presigned URL when there is
 * no quality variant, or a label -> URL map when several variants exist.
 */
export async function resolveVideoUrl(
	key: string,
	allKeys: string[],
	s3Client: S3Client,
	bucketName: string,
): Promise<VideoUrl> {
	const variantEntries = Object.entries(
		getQualityVariantsFromList(key, allKeys),
	);

	if (variantEntries.length === 0) {
		return createPresignedDownloadUrl(s3Client, bucketName, key);
	}

	if (variantEntries.length === 1) {
		return createPresignedDownloadUrl(
			s3Client,
			bucketName,
			variantEntries[0][1],
		);
	}

	const urls = await Promise.all(
		variantEntries.map(([, variantKey]) =>
			createPresignedDownloadUrl(s3Client, bucketName, variantKey),
		),
	);
	return Object.fromEntries(
		variantEntries.map(([label], index) => [label, urls[index]]),
	);
}

export async function buildPlaylistList(
	currentFile: AccessibleObject,
	allFiles: AccessibleObject[],
	s3Client: S3Client,
	bucketName: string,
): Promise<PlaylistItem[]> {
	const playlistName = currentFile.allTags?.["playlist"]?.split("@@")[0]; // getting the playlist name of the current video
	if (!playlistName) return [];

	const playlistFiles = allFiles.filter(
		// gettting all the files with the same playlist name to build the playlist
		file => file.allTags?.["playlist"]?.split("@@")[0] === playlistName,
	);

	if (playlistFiles.length <= 1) return [];

	playlistFiles.sort((a, b) => {
		// sorting the playlist based on the position
		const posA = Number(a.allTags?.["playlist"]?.split("@@")[1]); // extract the position suffix from the playlist tag
		const posB = Number(b.allTags?.["playlist"]?.split("@@")[1]); // extract the position suffix from the playlist tag
		if (isNaN(posA) && isNaN(posB)) return 0;
		if (isNaN(posA)) return 1;
		if (isNaN(posB)) return -1;
		return posA - posB;
	});

	const allKeys = allFiles.map(f => f.key);
	const playlistItems: PlaylistItem[] = [];
	for (const file of playlistFiles) {
		const videoUrl = await resolveVideoUrl(
			file.key,
			allKeys,
			s3Client,
			bucketName,
		);

		playlistItems.push({
			key: file.key,
			title: file.allTags?.["description"] || file.key,
			description: file.allTags?.["description"],
			videoUrl,
			playlistName,
			playlistPosition: Number(
				file.allTags?.["playlist"]?.split("@@")[1],
			),
		});
	}
	return playlistItems;
}
